import React from 'react';
import { Beer, CreditCard, Coffee, IceCreamCone, Phone, Truck } from 'lucide-react';
import { PageLayout } from '../components/global/PageLayout';
import { PageIntro, Section } from '../components/ui/PageIntro';
import { ImageBox } from '../components/ui/ImageBox';
import { CTABlock } from '../components/ui/CTABlock';
import { BAR_NOTE, CAKES, DRINKS, HOT_FOOD_NOTE, ICE_CREAM, MENU_AVAILABILITY_NOTE, PAYMENT_NOTE, menuSections } from '../data/menu';
import { IMAGES } from '../data/images';
import { CLOSED_DAY_NOTE, CONTACT, OPENING_HOURS, OPENING_HOURS_NOTE, PAGE_META } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function FoodAndDrink() {
  useDocumentMeta({
    ...PAGE_META.foodAndDrink,
    path: '/food-and-drink'
  });
  return <PageLayout data-id="element-570" data-mp="1uq8knjdpldva">
      <PageIntro eyebrow="Food & Drink" title="Refuel Properly" intro="All our hot food comes from the Fuel Stop, our catering truck out in the car park, doing proper baps, handmade pies and the smashed burger every day we're open. Inside there's the coffee bar, a cabinet of cakes, the ice cream counter and a proper bar. Nothing needs ordering online, so just come and have a look at the boards." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Food & drink'
    }]} image={IMAGES.fuelStopHatch.src} data-id="element-571" data-mp="1uqh11l91j1b8" />

      <Section data-id="element-572" data-mp="1e5vbjh5sws5h">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:gap-14" data-id="element-573" data-mp="1e5c44uds1m5p">
          {/* The menu is what the page is for, so it gets the width and the
              weight. Everything else is a sidebar. */}
          <div data-id="element-574" data-mp="1nvwxa2aolyh8">
            {/* The bar, not the hatch: the hatch is already the head band
                above, and repeating it here would spend the best position
                on the page saying nothing new. */}
            <figure className="overflow-hidden rounded-container border border-hairline bg-white" data-id="element-575" data-mp="ve2ric8pmh9w">
              <ImageBox src={IMAGES.theBar.src} alt={IMAGES.theBar.alt} ratio="16/9" data-id="element-576" data-mp="qfhpq8dt7pjc" />
              <figcaption className="border-t border-hairline px-4 py-3 text-sm text-ink/65" data-id="element-577" data-mp="1k60brwiir7uy">
                The main bar inside, guitars made from bike chain and all.
              </figcaption>
            </figure>

            {/* The most consequential fact on the page, so it sits at the
                top of it. Someone riding over for a sit-down hot meal needs
                to know the kitchen is not fitted yet. Registry D43. */}
            <section aria-labelledby="hot-food-heading" className="mt-6 rounded-container border border-moss/25 bg-moss/10 p-5" data-id="element-578" data-mp="11876wooc4nux">
              <h2 id="hot-food-heading" className="flex items-center gap-2.5 font-bebas text-2xl tracking-wide text-ink" data-id="element-579" data-mp="1b521wxleg64g">
                <Truck className="h-6 w-6 text-moss" aria-hidden="true" data-id="element-580" data-mp="2hnibs2sd3rav" />
                {HOT_FOOD_NOTE.heading}
              </h2>
              <p className="mt-2 max-w-prose leading-relaxed text-ink/85" data-id="element-581" data-mp="axooen9z02et">
                {HOT_FOOD_NOTE.body}
              </p>
              <p className="mt-3 max-w-prose border-t border-moss/25 pt-3 text-sm leading-relaxed text-ink/75" data-id="element-582" data-mp="5zde6bfrjpw2">
                {HOT_FOOD_NOTE.kitchenNote}
              </p>
            </section>

            {/* Card only is real signage at the hatch. Someone who rides out
                expecting to pay cash has a wasted journey, so this is given
                its own line rather than buried in a footnote. */}
            <p className="mt-3 flex items-start gap-3 rounded-container border border-flame/30 bg-flame/10 p-4 font-bold leading-relaxed text-ink" data-id="element-583" data-mp="3jbnh9g1nb7o3">
              <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-flame" aria-hidden="true" data-id="element-584" data-mp="39fap4mlr11vg" />
              <span data-id="element-585" data-mp="1clz58o0rp3di">{PAYMENT_NOTE}</span>
            </p>

            {/* The menu, exactly as it reads on the board. */}
            <section aria-labelledby="fuel-stop-menu-title" className="mt-10 rounded-container border border-hairline bg-white p-5 sm:p-8" data-id="element-586" data-mp="17lbem3ggaojt">
              <div className="border-b border-hairline pb-4" data-id="element-587" data-mp="1kmoql0wto1az">
                <h2 id="fuel-stop-menu-title" className="font-bebas text-4xl leading-none tracking-wide text-ink sm:text-5xl" data-id="element-588" data-mp="15qpivrdmk36d">
                  Fuel Stop Menu
                </h2>
                <p className="mt-2 text-sm text-ink/65" data-id="element-589" data-mp="ww1gc8zfozl9">
                  Order at the truck in the car park, every day we're open.
                  The same menu is on both boards.
                </p>
              </div>

              <div className="mt-8 space-y-10" data-id="element-590" data-mp="mxlkjspgp25f">
                {menuSections.map(section => <section key={section.heading} data-id="element-591" data-mp="rw6mbwkd3tvz">
                    <div className="flex flex-wrap items-baseline gap-x-3 border-b-2 border-flame pb-2" data-id="element-592" data-mp="dzt6ssze22k5">
                      <h3 className="font-bebas text-2xl tracking-wide text-ink" data-id="element-593" data-mp="hz1ylsavbqd2">
                        {section.heading}
                      </h3>
                      {section.supportingText && <p className="text-sm font-bold uppercase tracking-[0.1em] text-moss" data-id="element-594" data-mp="3qjnp4q9e4flk">
                          {section.supportingText}
                        </p>}
                    </div>

                    {/* A price list is label-and-value pairs, so a dl. The
                        dotted leader is the printed-menu convention and
                        keeps long names tied to their price without a
                        table. */}
                    <dl className="divide-y divide-hairline" data-id="element-595" data-mp="r3h28b1jr5n0">
                      {section.items.map(item => <div key={item.name} className="py-4" data-id="element-596" data-mp="n2x351xexry8">
                          <div className="flex items-baseline gap-3" data-id="element-597" data-mp="110vhw8fnbqe9">
                            <dt className="font-bold text-ink" data-id="element-598" data-mp="10z28ildnes2k">{item.name}</dt>
                            <span aria-hidden="true" className="mx-1 min-w-4 flex-1 translate-y-[-0.2em] border-b border-dotted border-ink/25" data-id="element-599" data-mp="w453rqnej4vh" />
                            <dd className="shrink-0 font-bebas text-2xl leading-none tracking-wide text-ink" data-id="element-600" data-mp="2pkjghlr12qs">
                              {item.price}
                            </dd>
                          </div>

                          {item.description && <p className="mt-1.5 text-sm leading-relaxed text-ink/70" data-id="element-601" data-mp="cf44ntwxp8ot">
                              {item.description}
                            </p>}
                          {item.choices && <p className="mt-1.5 text-sm leading-relaxed text-ink/70" data-id="element-602" data-mp="qewsnhh2tat3">
                              <span className="font-bold text-ink" data-id="element-603" data-mp="2n7ggk44jt9k8">
                                Choose from:
                              </span>{' '}
                              {item.choices.join(' · ')}
                            </p>}
                        </div>)}
                    </dl>

                    {section.footnote && <p className="text-sm text-ink/70" data-id="element-604" data-mp="2ax78b03d4iq1">{section.footnote}</p>}
                  </section>)}
              </div>

              {/* Availability and ingredient query notice. No dietary claims
                  are made. See registry D09. */}
              <p className="mt-8 border-l-4 border-flame bg-cream px-4 py-3 text-sm leading-relaxed text-ink/80" data-id="element-605" data-mp="33jii91cuzvzi">
                {MENU_AVAILABILITY_NOTE}
              </p>

              <a href={CONTACT.telephoneHref} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-container bg-flame px-6 py-3.5 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 sm:w-auto" data-id="element-606" data-mp="2tlvpbayq63wj">
                <Phone className="h-4 w-4" aria-hidden="true" data-id="element-607" data-mp="3dadhpv1pieat" />
                Give us a ring on {CONTACT.telephone}
              </a>
            </section>

            {/* Inside the building, as opposed to at the hatch. The drinks
                are transcribed from the board in the photograph; no prices,
                because the board's are not legible in it. */}
            <section aria-labelledby="counter-title" className="mt-10 overflow-hidden rounded-container border border-hairline bg-white" data-id="element-608" data-mp="vcnaesn6kwe">
              <div className="grid sm:grid-cols-2" data-id="element-609" data-mp="3nae0nfvnyseh">
                {/* The square-on board shot, because it is the evidence for
                    the prices printed directly beneath it. */}
                <ImageBox src={IMAGES.drinksBoardPrices.src} alt={IMAGES.drinksBoardPrices.alt} ratio="4/3" className="h-full" data-id="element-610" data-mp="jsm37lyrdg8w" />
                <ImageBox src={IMAGES.cakeCabinet.src} alt={IMAGES.cakeCabinet.alt} ratio="4/3" className="h-full border-l border-hairline" data-id="element-611" data-mp="xxmglk0q42zd" />
              </div>

              <div className="p-5 sm:p-8" data-id="element-612" data-mp="17qbqiagml8sl">
                <h2 id="counter-title" className="flex items-center gap-2.5 font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-613" data-mp="12p4kafu4sho1">
                  <Coffee className="h-7 w-7 text-rust" aria-hidden="true" data-id="element-614" data-mp="ayq76ltxz9df" />
                  {DRINKS.heading}
                </h2>
                <p className="mt-1 text-sm font-bold uppercase tracking-[0.1em] text-moss" data-id="element-615" data-mp="ozkk9hrxpyif">
                  {DRINKS.supportingText}
                </p>

                {/* Same dotted-leader treatment as the Fuel Stop boards, so
                    the two priced lists on this page read as one system. */}
                <dl className="mt-5 grid gap-x-10 sm:grid-cols-2" data-id="element-616" data-mp="1vdtleev6smzs">
                  {DRINKS.items.map(item => <div key={item.name} className="flex items-baseline gap-3 border-b border-hairline py-2.5" data-id="element-617" data-mp="or2rhxugewt8">
                      <dt className="font-bold text-ink" data-id="element-618" data-mp="exu7udz228x2">{item.name}</dt>
                      <span aria-hidden="true" className="mx-1 min-w-4 flex-1 translate-y-[-0.2em] border-b border-dotted border-ink/25" data-id="element-619" data-mp="9zap0mfrdd8x" />
                      <dd className="shrink-0 font-bebas text-xl leading-none tracking-wide text-ink" data-id="element-620" data-mp="yun3henye66">
                        {item.price}
                      </dd>
                    </div>)}
                </dl>

                <div className="mt-8 border-t border-hairline pt-6" data-id="element-621" data-mp="3n8yo21dykxnh">
                  <h3 className="font-bebas text-2xl tracking-wide text-ink" data-id="element-622" data-mp="1qdahxdg6s2mo">
                    {CAKES.heading}
                  </h3>
                  <p className="mt-2 max-w-prose leading-relaxed text-ink/80" data-id="element-623" data-mp="1ldoa5jj4nel9">
                    {CAKES.body}
                  </p>
                  <p className="mt-2 text-sm font-bold text-ink/60" data-id="element-624" data-mp="1xxbfhwfymwrz">
                    {CAKES.priceNote}
                  </p>
                </div>

                {/* The photographs show taps, spirits and a wine fridge, so
                    the bar is stated. What is actually on, and the licensing
                    hours, are client facts and are not invented here. */}
                <div className="mt-6 flex items-start gap-3 rounded-container border border-moss/25 bg-moss/10 p-4" data-id="element-625" data-mp="1eyz4e55hnctr">
                  <Beer className="mt-0.5 h-5 w-5 shrink-0 text-moss" aria-hidden="true" data-id="element-626" data-mp="1jy2alsx7fp3b" />
                  <div data-id="element-627" data-mp="1ovbw4xhh3glv">
                    <h3 className="font-bold text-ink" data-id="element-628" data-mp="2vdh42mq7fyto">{BAR_NOTE.heading}</h3>
                    <p className="mt-1 leading-relaxed text-ink/80" data-id="element-629" data-mp="2vfl8d5s0mzuh">
                      {BAR_NOTE.body}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* The ice cream counter is inside rather than at the hatch, and
                prices are on its own display, so it is a block of its own
                rather than a fourth section with invented figures. */}
            <section aria-labelledby="ice-cream-title" className="mt-10 overflow-hidden rounded-container border border-hairline bg-white" data-id="element-630" data-mp="2fmzkgihervzn">
              <div className="grid sm:grid-cols-2" data-id="element-631" data-mp="1mz3sfr2sas97">
                <ImageBox src={IMAGES.iceCreamCounter.src} alt={IMAGES.iceCreamCounter.alt} ratio="4/3" className="h-full" data-id="element-632" data-mp="1n0exdu7yu61w" />
                <ImageBox src={IMAGES.iceCreamCone.src} alt={IMAGES.iceCreamCone.alt} ratio="4/3" className="h-full border-l border-hairline" data-id="element-633" data-mp="1wuyg50lv7diu" />
              </div>

              <div className="p-5 sm:p-8" data-id="element-634" data-mp="3fvft5cx70ob0">
                <h2 id="ice-cream-title" className="flex items-center gap-2.5 font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-635" data-mp="3fuefd0oslnpl">
                  <IceCreamCone className="h-7 w-7 text-rust" aria-hidden="true" data-id="element-636" data-mp="35z4dccye9gbt" />
                  {ICE_CREAM.heading}
                </h2>
                <p className="mt-1 text-sm font-bold uppercase tracking-[0.1em] text-moss" data-id="element-637" data-mp="2hckyxgciud3">
                  {ICE_CREAM.supportingText}
                </p>
                <p className="mt-3 max-w-prose leading-relaxed text-ink/80" data-id="element-638" data-mp="3pm24uw1sguof">
                  {ICE_CREAM.body}
                </p>

                {/* One price for everything, so a dotted-leader list would
                    repeat "£3.50" six times. The flavours are a plain list
                    with the price stated once above them. */}
                <p className="mt-4 font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-639" data-mp="2i9a568p0sfxw">
                  {ICE_CREAM.priceNote}
                </p>
                <ul className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2" data-id="element-640" data-mp="2to3n28t906g5">
                  {ICE_CREAM.flavours.map(flavour => <li key={flavour} className="flex items-baseline gap-2.5 border-b border-hairline py-2 text-sm" data-id="element-641" data-mp="2optp5qg52rqu">
                      <span aria-hidden="true" className="font-bold text-rust" data-id="element-642" data-mp="3hcwje6ds8gba">
                        ●
                      </span>
                      <span className="font-medium text-ink" data-id="element-643" data-mp="37egsa3er971s">{flavour}</span>
                    </li>)}
                </ul>
                <p className="mt-4 text-sm leading-relaxed text-ink/65" data-id="element-644" data-mp="33gj85ezudb59">
                  {ICE_CREAM.placeholderNote}
                </p>
              </div>
            </section>

            {/* Allergens are deliberately not listed. Getting them wrong in
                print is worse than sending people to ask. */}
            <div className="mt-10 rounded-container border border-hairline bg-white p-5" data-id="element-645" data-mp="biaur4a6jc9x">
              <h2 className="font-bebas text-2xl tracking-wide text-ink" data-id="element-646" data-mp="ghxabkdhd4lw">
                Allergies and dietary needs
              </h2>
              <p className="mt-2 max-w-prose leading-relaxed text-ink/75" data-id="element-647" data-mp="l9f8k0o4cr36">
                Do have a word with us before you order, at the truck or at
                the counter. We handle nuts, gluten, dairy and sesame in a
                small space, so we won't claim in writing that anything is
                free from them. Whoever is serving will gladly talk you
                through what goes into each thing.
              </p>
            </div>
          </div>

          {/* Static, not sticky. A photograph and the opening hours are
              reference material you read once, so following the reader down
              a long menu is movement that buys nothing. */}
          <aside className="space-y-6" data-id="element-648" data-mp="d3cnmt4grtzm">
            <figure className="overflow-hidden rounded-container border border-hairline bg-white" data-id="element-649" data-mp="d4e6hjrq1krw">
              <ImageBox src={IMAGES.icedCoffees.src} alt={IMAGES.icedCoffees.alt} ratio="4/5" data-id="element-650" data-mp="86w0o2903lo4" />
              <figcaption className="border-t border-hairline px-4 py-3 text-sm text-ink/65" data-id="element-651" data-mp="h5g8279428x1">
                Iced coffees, out on the deck.
              </figcaption>
            </figure>

            <div className="rounded-container border border-hairline bg-white p-6" data-id="element-652" data-mp="r4f6yy42qq6u">
              <h2 className="font-bebas text-2xl tracking-wide text-ink" data-id="element-653" data-mp="14v27mvteaq5k">
                Serving hours
              </h2>
              {/* The truck runs the café's hours rather than its own, so
                  one set of times covers both. Worth stating, because a
                  catering truck is normally an occasional thing. */}
              <p className="mt-1 text-sm leading-relaxed text-ink/65" data-id="element-654" data-mp="19vpwbau5iuwm">
                The truck and the counter both run these hours.
              </p>
              <dl className="mt-4 space-y-2.5 text-sm" data-id="element-655" data-mp="1yf9vw00brgje">
                {OPENING_HOURS.map(row => <div key={row.days} className="flex justify-between gap-4" data-id="element-656" data-mp="1ji9a0d4tefsu">
                    <dt className="font-bold text-ink/80" data-id="element-657" data-mp="1jkdbkof8r2py">{row.days}</dt>
                    <dd className="shrink-0 text-ink/65" data-id="element-658" data-mp="1ofj07v012vhq">{row.hours}</dd>
                  </div>)}
              </dl>
              {/* Monday gets its own line above the note. It is the one
                  row here that turns a ride out into a wasted journey. */}
              <p className="mt-4 border-t border-hairline pt-3 text-sm font-bold text-ink" data-id="element-659" data-mp="10subapclq2rm">
                {CLOSED_DAY_NOTE}
              </p>
              <p className="mt-2 text-sm text-ink/60" data-id="element-660" data-mp="15o9kv53ktci8">{OPENING_HOURS_NOTE}</p>
            </div>

            <div className="rounded-container bg-ink p-6" data-id="element-661" data-mp="rt7941c1ssxi">
              <h2 className="font-bebas text-2xl tracking-wide text-cream" data-id="element-662" data-mp="vr4zv8lgvn30">
                Group bookings
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-cream/80" data-id="element-663" data-mp="1c5uxu6bo2vh6">
                Club meets, birthdays or a ride-out passing through. Give us a
                ring ahead and we'll make sure we're ready for you.
              </p>
              <a href={CONTACT.telephoneHref} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-container bg-flame px-5 py-3.5 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-664" data-mp="xb6js3s7aopy">
                <Phone className="h-4 w-4" aria-hidden="true" data-id="element-665" data-mp="ekymi2b6msho" />
                {CONTACT.telephone}
              </a>
            </div>
          </aside>
        </div>
      </Section>

      <CTABlock data-id="element-666" data-mp="5jr28o5i3s6l" />
    </PageLayout>;
}